/**
 * My Roles panel UI generation and interaction handlers
 */

import { chartInstance } from '../state.js';
import { findD3NodeById } from '../utils/hierarchyUtils.js';
import { t } from '../utils/i18n.js';

/**
 * Generate HTML for My Roles overview panel
 */
export function generateMyRolesHtml(claimedRoleIds: number[], roles: Array<{ id: number, name: string }>): string {
    let html = `<div class="role-panel__my-roles-overview"><h3>${t('myRoles.title')}</h3>`;
    
    if (claimedRoleIds.length === 0) {
        html += `<p class="role-panel__empty-state">${t('myRoles.noClaims')}</p>`;
    } else {
        html += '<ul class="role-panel__my-roles-list">';
        for (const role of roles) {
            html += `<li class="role-panel__my-role-item" data-role-id="${role.id}" data-role-name="${role.name}">${role.name}</li>`;
        }
        html += '</ul>';
    }
    
    html += '</div>';
    return html;
}

/**
 * Highlight circles for claimed roles
 */
export function highlightClaimedRoles(claimedRoleIds: number[]): void {
    const svg = document.getElementById('chart')?.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('circle').forEach(circle => {
        const circleData = (circle as any).__data__;
        if (circleData && circleData.data && claimedRoleIds.includes(circleData.data.id)) {
            circle.classList.add('role-panel__highlighted-myrole');
        } else {
            circle.classList.remove('role-panel__highlighted-myrole');
        }
    });
}

/**
 * Add hover highlight to circles matching role ID
 */
function addHoverHighlight(roleId: string): void {
    const svg = document.getElementById('chart')?.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('circle').forEach(circle => {
        const circleData = (circle as any).__data__;
        if (circleData && circleData.data && String(circleData.data.id) === String(roleId)) {
            circle.classList.add('highlighted-hover');
        }
    });
}

/**
 * Remove all hover highlights
 */
function removeHoverHighlights(): void {
    const svg = document.getElementById('chart')?.querySelector('svg');
    if (!svg) return;

    svg.querySelectorAll('circle.highlighted-hover').forEach(circle => {
        circle.classList.remove('highlighted-hover');
    });
}

/**
 * Focus on a role (zoom and display in panel)
 */
async function focusOnRole(roleId: string): Promise<void> {
    // Show loading
    const panelContent = document.querySelector('.panel-content');
    if (panelContent) {
        panelContent.innerHTML = `<div class="loading-overlay"><div class="loading-spinner"></div>${t('myRoles.loadingDetails')}</div>`;
    }

    try {
        const roleRes = await fetch(`/api/roles/${roleId}`, { credentials: 'include' });
        if (!roleRes.ok) return;

        const role = await roleRes.json();

        if (chartInstance && typeof chartInstance.zoomToNode === 'function') {
            const dataRoot = typeof chartInstance.data === 'function' ? chartInstance.data() : null;
            const hierarchyRoot = (dataRoot as any)?.__dataNode;

            if (hierarchyRoot) {
                const d3Node = findD3NodeById(hierarchyRoot, parseInt(roleId));
                if (d3Node) {
                    chartInstance.zoomToNode(d3Node.data, { __dataNode: d3Node });

                    // Display role details in panel
                    if (typeof (window as any).displayNodeData === 'function' && d3Node.data) {
                        (window as any).displayNodeData(d3Node.data);
                    }

                    // Re-apply highlighting after zoom
                    setTimeout(() => {
                        if (typeof (window as any).reapplyMyRolesHighlight === 'function') {
                            (window as any).reapplyMyRolesHighlight();
                        }
                    }, 300);
                }
            }
        }
    } catch (error) {
        console.error('Failed to focus role:', error);
    }
}

/**
 * Attach event handlers to My Roles list items
 */
export function attachMyRolesItemHandlers(panelContent: HTMLElement): void {
    panelContent.querySelectorAll('.role-panel__my-role-item').forEach(item => {
        // Hover handlers
        item.addEventListener('mouseenter', () => {
            const roleId = item.getAttribute('data-role-id');
            if (roleId) addHoverHighlight(roleId);
        });

        item.addEventListener('mouseleave', () => {
            removeHoverHighlights();
        });

        // Click handler
        item.addEventListener('click', async () => {
            const roleId = item.getAttribute('data-role-id');
            if (!roleId) return;

            removeHoverHighlights();
            await focusOnRole(roleId);
        });
    });
}
